// Fahrzeug-Statistik-Kategorien — Developer-UI
// Ordnet jedem Fahrzeug eine Statistik-Kategorie zu (RD / LHF / Sonstige) oder Auto-Default.
import React, { useEffect, useState } from 'react';
import { Tags as TagsIcon, Save, RotateCcw, Info } from 'lucide-react';
import { toast } from 'sonner';
import { developerApi } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { cn } from '../../lib/utils';

const KAT_FARBEN = {
  RD: 'bg-rose-100 text-rose-800 border-rose-300',
  LHF: 'bg-sky-100 text-sky-800 border-sky-300',
  Sonstige: 'bg-stone-100 text-stone-800 border-stone-300',
};

export default function FahrzeugKategorienSection() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await developerApi.getFahrzeugeKategorien();
      setItems(res.data || []);
    } catch (e) {
      toast.error('Konnte Fahrzeug-Liste nicht laden');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateKategorie = async (id, value) => {
    setSavingId(id);
    try {
      // value === "AUTO" → null senden (zurück auf Default)
      const payload = value === 'AUTO' ? null : value;
      await developerApi.updateFahrzeugKategorie(id, payload);
      toast.success('Kategorie gespeichert');
      await load();
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  };

  // Gruppieren nach Wache
  const grouped = items.reduce((acc, it) => {
    const key = it.wache_name || '—';
    if (!acc[key]) acc[key] = { wache: key, items: [] };
    acc[key].items.push(it);
    return acc;
  }, {});

  return (
    <Card data-testid="fahrzeug-kategorien-section">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <TagsIcon className="w-5 h-5" /> Statistik-Kategorien
            </CardTitle>
            <CardDescription>
              Ordnet jedem Fahrzeug eine Kategorie für die Statistik-Übersicht zu.
              <span className="block text-xs mt-1">Standard: <b>Auto</b> (aus Typ abgeleitet). Override nur bei Sonderfällen nötig.</span>
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={isLoading} data-testid="reload-kategorien-btn">
            <RotateCcw className={cn('w-4 h-4 mr-2', isLoading && 'animate-spin')} /> Neu laden
          </Button>
        </div>
        <div className="mt-2 flex items-start gap-2 text-xs bg-muted/40 rounded-md p-2 border">
          <Info className="w-4 h-4 shrink-0 mt-0.5 text-muted-foreground" />
          <div className="space-y-1">
            <p><Badge className={cn('mr-1', KAT_FARBEN.RD)}>RD</Badge> Rettungsdienst: RTW, NEF, KTW</p>
            <p><Badge className={cn('mr-1', KAT_FARBEN.LHF)}>LHF</Badge> Löschzug: LHF, HLF, TLF. Springer hängen by-default an LHF.</p>
            <p><Badge className={cn('mr-1', KAT_FARBEN.Sonstige)}>Sonstige</Badge> alles andere (DLK, GW, RW, etc.)</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.values(grouped).map(g => (
          <div key={g.wache} className="rounded-lg border overflow-hidden">
            <div className="bg-muted/50 px-3 py-2 border-b">
              <p className="text-sm font-semibold">{g.wache}</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Funkrufname</TableHead>
                  <TableHead className="w-20">Typ</TableHead>
                  <TableHead className="w-24">Springer</TableHead>
                  <TableHead className="w-32">Aktuell</TableHead>
                  <TableHead className="w-48">Override setzen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {g.items.map(fz => (
                  <TableRow key={fz.id} data-testid={`fz-kategorie-row-${fz.id}`}>
                    <TableCell className="font-medium">{fz.funkrufname}</TableCell>
                    <TableCell className="font-mono text-xs">{fz.typ || '—'}</TableCell>
                    <TableCell>
                      {fz.ist_springer ? (
                        <Badge className="bg-orange-100 text-orange-800 border-orange-300">Ja</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('border', KAT_FARBEN[fz.kategorie_effektiv] || '')}>
                        {fz.kategorie_effektiv}
                      </Badge>
                      {!fz.kategorie_override && (
                        <span className="ml-1 text-[10px] text-muted-foreground">(auto)</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={fz.kategorie_override || 'AUTO'}
                        onValueChange={(v) => updateKategorie(fz.id, v)}
                        disabled={savingId === fz.id}
                      >
                        <SelectTrigger className="h-8 text-xs" data-testid={`fz-kategorie-select-${fz.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AUTO">Auto (Default)</SelectItem>
                          <SelectItem value="RD">RD — Rettungsdienst</SelectItem>
                          <SelectItem value="LHF">LHF — Löschzug</SelectItem>
                          <SelectItem value="Sonstige">Sonstige</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ))}
        {!isLoading && items.length === 0 && (
          <p className="text-sm text-muted-foreground italic text-center py-6">Keine Fahrzeuge gefunden.</p>
        )}
      </CardContent>
    </Card>
  );
}
